import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useDispatch, useSelector } from 'react-redux';
import {
    setOperationStep,
    setOperationData,
    resetOperation,
    setCommandMessage
} from '../../../redux/operationSlice';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { useInputText } from '../../../utils/useInputText';
import { COMMANDS } from '../../../utils/commonProperties';
import { createDrawingClickHandler } from "./useDrawingEvents";
import { getSnappedPoint } from '../../../shared/snap/getSnappedPoint';
import { createSnapMarker } from '../../../shared/snap/createSnapMarker';
import { createEscapeHandler } from '../../../utils/escapeHandler';
import { DrawCommand } from '../../../modules/undoRedo';
import { addUserEntity } from '../../../redux/dxfSlice';

const useDrawCircle = (scene, camera, renderer, snapPoints) => {
    const tempCircleRef = useRef(null);
    const snapMarkerRef = useRef(null);
    const inputBuffer = useRef('');
    const lastMouse = useRef({ x: 0, y: 0 });
    const snapMode = useSelector((state) => state.mods.snapMode);

    const dispatch = useDispatch();
    const inputText = useInputText(scene, camera);

    const { commandType, step, data, drawMethod } = useSelector(state => state.operation);
    const { gridMode, objectSnap } = useSelector(state => state.mods);
    const { activeLayerName } = useSelector(state => state.layer);
    const activeTabId = useSelector(state => state.projectTabs.activeTabId);

    const getMousePointOnPlane = (mouseNDC) => {
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouseNDC, camera);
        const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
        const worldPoint = new THREE.Vector3();
        raycaster.ray.intersectPlane(plane, worldPoint);
        return worldPoint;
    };

    useEffect(() => {
        if (commandType !== COMMANDS.DRAW_CIRCLE || !scene || !camera || !renderer) return;

        const domElement = renderer.domElement;
        const method = drawMethod || 'center_radius';

        const circumscribedCircle = (p1, p2, p3) => {
            const ax = p1.x, ay = p1.y, bx = p2.x, by = p2.y, cx = p3.x, cy = p3.y;
            const D = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
            if (Math.abs(D) < 1e-10) return null;
            const ux = ((ax*ax+ay*ay)*(by-cy) + (bx*bx+by*by)*(cy-ay) + (cx*cx+cy*cy)*(ay-by)) / D;
            const uy = ((ax*ax+ay*ay)*(cx-bx) + (bx*bx+by*by)*(ax-cx) + (cx*cx+cy*cy)*(bx-ax)) / D;
            const center = new THREE.Vector3(ux, uy, 0);
            return { center, radius: center.distanceTo(p1) };
        };

        const createCircleMesh = (center, radius, color) => {
            const segments = 64;
            const angleStep = (Math.PI * 2) / segments;
            const points = [];
            for (let i = 0; i <= segments; i++) {
                const angle = i * angleStep;
                points.push(new THREE.Vector3(
                    center.x + radius * Math.cos(angle),
                    center.y + radius * Math.sin(angle),
                    0
                ));
            }
            const geometry = new THREE.BufferGeometry().setFromPoints(points);
            const material = new THREE.LineBasicMaterial({ color });
            return new THREE.LineLoop(geometry, material);
        };

        const removeTempCircle = () => {
            if (tempCircleRef.current) {
                scene.remove(tempCircleRef.current);
                tempCircleRef.current.geometry.dispose();
                tempCircleRef.current.material.dispose();
                tempCircleRef.current = null;
            }
        };

        const finalizeCircle = (center, radius) => {
            const circle = createCircleMesh(center, radius, 0xffffff);
            circle.userData = {
                id: generateUniqueId(),
                isSelectable: true,
                type: "circle",
                center: { x: center.x, y: center.y, z: 0 },
                radius: radius,
                layerName: activeLayerName,
            };
            scene.add(circle);

            // Redux'a DXF entity olarak kaydet (JBZ'ye dahil edilecek)
            dispatch(addUserEntity({
                type: 'CIRCLE',
                center: { x: center.x, y: center.y, z: center.z || 0 },
                radius: radius,
                layer: activeLayerName || '0',
                _userEntityId: circle.userData.id,
                _userDrawn: true,
                _tabId: activeTabId || 'mimari',
            }));

            // Undo-redo kaydı
            const manager = window.undoRedoManager;
            if (manager) {
                const tx = manager.beginTransaction('Draw Circle');
                manager.addCommand(new DrawCommand(circle));
                manager.commitTransaction(tx);
            }

            removeTempCircle();
            inputBuffer.current = '';
            dispatch(resetOperation());
            dispatch(setCommandMessage(""));
            inputText.clear();
        };

        // Initial message
        if (method === '2point' || method === '3point') {
            dispatch(setCommandMessage("1. noktayı seçin"));
        } else {
            dispatch(setCommandMessage("Merkez noktasını seçin"));
        }

        const handleCirclePoint = (point) => {
            if (method === 'center_radius' || method === 'center_diameter') {
                if (step === 0) {
                    dispatch(setOperationData({ center: { x: point.x, y: point.y, z: point.z } }));
                    dispatch(setOperationStep(1));
                    dispatch(setCommandMessage(
                        method === 'center_diameter'
                            ? "Çap için nokta seçin veya sayı girin"
                            : "Yarıçap için nokta seçin veya sayı girin"
                    ));
                } else if (step === 1) {
                    const center = new THREE.Vector3(data.center.x, data.center.y, data.center.z);
                    const distance = center.distanceTo(point);
                    const radius = method === 'center_diameter' ? distance / 2 : distance;
                    finalizeCircle(center, radius);
                }
            } else if (method === '2point') {
                if (step === 0) {
                    dispatch(setOperationData({ p1: { x: point.x, y: point.y, z: point.z } }));
                    dispatch(setOperationStep(1));
                    dispatch(setCommandMessage("2. noktayı seçin"));
                } else if (step === 1) {
                    const p1 = new THREE.Vector3(data.p1.x, data.p1.y, data.p1.z);
                    const p2 = point.clone();
                    const center = p1.clone().add(p2).multiplyScalar(0.5);
                    const radius = p1.distanceTo(p2) / 2;
                    finalizeCircle(center, radius);
                }
            } else if (method === '3point') {
                if (step === 0) {
                    dispatch(setOperationData({ p1: { x: point.x, y: point.y, z: point.z } }));
                    dispatch(setOperationStep(1));
                    dispatch(setCommandMessage("2. noktayı seçin"));
                } else if (step === 1) {
                    dispatch(setOperationData({ ...data, p2: { x: point.x, y: point.y, z: point.z } }));
                    dispatch(setOperationStep(2));
                    dispatch(setCommandMessage("3. noktayı seçin"));
                } else if (step === 2) {
                    const p1 = new THREE.Vector3(data.p1.x, data.p1.y, data.p1.z);
                    const p2 = new THREE.Vector3(data.p2.x, data.p2.y, data.p2.z);
                    const p3 = point.clone();
                    const result = circumscribedCircle(p1, p2, p3);
                    if (result) {
                        finalizeCircle(result.center, result.radius);
                    }
                }
            }

            inputText.clear();
        };

        const handleClick = createDrawingClickHandler({
            scene,
            camera,
            renderer,
            snapPoints,
            snapMarkerRef,
            gridMode,
            objectSnap,
            snapMode,
            onPointSelected: handleCirclePoint
        });

        const handleMouseMove = (e) => {
            if (!scene || !camera || !renderer) return;

            const rect = domElement.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
            lastMouse.current = { x, y };
            const mouseNDC = new THREE.Vector2(x, y);
            const worldPoint = getMousePointOnPlane(mouseNDC);

            const { finalPoint, snapped, snapSource } = getSnappedPoint(
                mouseNDC,
                worldPoint,
                snapPoints,
                camera,
                renderer,
                { snapMode }
            );

            // Snap marker
            if (snapMarkerRef.current) {
                scene.remove(snapMarkerRef.current);
                snapMarkerRef.current.geometry.dispose();
                snapMarkerRef.current.material.dispose();
                snapMarkerRef.current = null;
            }

            if (snapMode && snapped && snapSource) {
                const marker = createSnapMarker(snapSource);
                marker.position.copy(finalPoint);
                scene.add(marker);
                snapMarkerRef.current = marker;
            }

            // Preview
            let previewCenter = null;
            let previewRadius = null;

            if (method === 'center_radius' && step === 1 && data.center) {
                const center = new THREE.Vector3(data.center.x, data.center.y, data.center.z);
                previewCenter = center;
                previewRadius = center.distanceTo(finalPoint);
            } else if (method === 'center_diameter' && step === 1 && data.center) {
                const center = new THREE.Vector3(data.center.x, data.center.y, data.center.z);
                previewCenter = center;
                previewRadius = center.distanceTo(finalPoint) / 2;
            } else if (method === '2point' && step === 1 && data.p1) {
                const p1 = new THREE.Vector3(data.p1.x, data.p1.y, data.p1.z);
                previewCenter = p1.clone().add(finalPoint).multiplyScalar(0.5);
                previewRadius = p1.distanceTo(finalPoint) / 2;
            } else if (method === '3point' && step === 2 && data.p1 && data.p2) {
                const p1 = new THREE.Vector3(data.p1.x, data.p1.y, data.p1.z);
                const p2 = new THREE.Vector3(data.p2.x, data.p2.y, data.p2.z);
                const result = circumscribedCircle(p1, p2, finalPoint);
                if (result) {
                    previewCenter = result.center;
                    previewRadius = result.radius;
                }
            }

            removeTempCircle();
            if (previewCenter && previewRadius && previewRadius > 0) {
                tempCircleRef.current = createCircleMesh(previewCenter, previewRadius, 0xffff00);
                scene.add(tempCircleRef.current);
            }

            // Input text tooltip for keyboard-input methods
            if ((method === 'center_radius' || method === 'center_diameter') && step === 1) {
                inputText.update(inputBuffer.current, lastMouse.current, {
                    backgroundColor: 0x4444ff,
                    color: 0xffffff,
                    force: true
                });
            }
        };

        const escapeHandler = createEscapeHandler({
            scene,
            dispatch,
            tempObjectRef: tempCircleRef,
            snapMarkerRef,
            inputBuffer,
            inputText,
            additionalCleanup: () => {
                dispatch(setCommandMessage(""));
            }
        });

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                escapeHandler(e);
            } else if (e.key === 'Enter' && step === 1 && inputBuffer.current &&
                       (method === 'center_radius' || method === 'center_diameter')) {
                const value = parseFloat(inputBuffer.current);
                if (!isNaN(value) && data.center) {
                    const center = new THREE.Vector3(data.center.x, data.center.y, data.center.z);
                    const radius = method === 'center_diameter' ? value / 2 : value;
                    finalizeCircle(center, radius);
                }
            } else if (/\d|\./.test(e.key)) {
                if (method === 'center_radius' || method === 'center_diameter') {
                    inputBuffer.current += e.key;
                    inputText.update(inputBuffer.current, lastMouse.current, {
                        backgroundColor: 0x4444ff,
                        color: 0xffffff,
                        force: true
                    });
                }
            } else if (e.key === 'Backspace') {
                if (method === 'center_radius' || method === 'center_diameter') {
                    inputBuffer.current = inputBuffer.current.slice(0, -1);
                    inputText.update(inputBuffer.current, lastMouse.current, {
                        backgroundColor: 0x4444ff,
                        color: 0xffffff,
                        force: true
                    });
                }
            }
        };

        domElement.addEventListener('click', handleClick);
        domElement.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            domElement.removeEventListener('click', handleClick);
            domElement.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [commandType, step, data, scene, camera, renderer, dispatch, gridMode, objectSnap, snapPoints, snapMode, drawMethod]);
};

export default useDrawCircle;
